import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  CalendarDays,
  CheckCircle,
  Clock,
  MapPin,
  Package,
  Truck,
  TruckIcon,
  Warehouse,
  Weight,
} from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { UpdateLoadModal } from "./components/UpdateLoadModal/UpdateLoadModal";
import { AddLoadModal } from "./components/AddLoadModal/MainComponent";
import { ViewLoadModal } from "./components/ViewLoadModal";
import { LoadingSpinner } from "@/components/Loading";
import { AddClaimModal } from "@/pages/claims/components/AddClaimModal";
import { LoadEvent } from "@/api/commonSchemas";
import { useAuthService } from "@/auth/AuthProvider";
import { AssignEditLoadModal } from "./components/AssignEditLoadModal";
import { getLoads, Load } from "@/api/loads/getLoads";

type LoadFilter = "All" | "Awaiting Pickup" | "Out For Delivery" | "Completed";

export function LoadsPage() {
  const [selectedLoad, setSelectedLoad] = useState<Load | null>(null);
  const [filter, setFilter] = useState<LoadFilter>("All");
  // ALL MODAL OPEN STATES
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [isAddLoadModalOpen, setIsAddLoadModalOpen] = useState(false);
  const [isEditViewOpen, setIsEditViewOpen] = useState(false);
  const [isAddClaimModalOpen, setIsAddClaimModalOpen] = useState(false);
  const { authState } = useAuthService();
  const orgType = authState?.orgType;

  const { data: unsortedLoads, isLoading } = useQuery({
    queryKey: ["loads", "receiver"],
    queryFn: () => getLoads(),
  });

  const statusOrder = [
    "Not Assigned",
    "Assigned",
    "Being Picked Up",
    "In Transit",
    "Delivered",
    "Cancelled",
  ] as const;

  const loads =
    unsortedLoads?.sort((a, b) => {
      const statusPriorityA = statusOrder.indexOf(a.status);
      const statusPriorityB = statusOrder.indexOf(b.status);

      if (statusPriorityA !== statusPriorityB) {
        return statusPriorityA - statusPriorityB;
      }

      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    }) || [];

  const formatDate = (dateTime: Date) => {
    return new Date(dateTime).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const formatTime = (dateTime: Date) => {
    return new Date(dateTime).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatWeight = (weight: number) => {
    return `${(weight / 1000).toFixed(1)}k lbs`;
  };

  const getBadgeColor = (status: string) => {
    switch (status) {
      case "Assigned":
        return "bg-blue-100 text-blue-800";
      case "In Transit":
        return "bg-yellow-100 text-yellow-800";
      case "Being Picked Up":
        return "bg-orange-100 text-orange-800";
      case "Delivered":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-200 text-gray-600";
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const lastLocation = (events: LoadEvent[]) => {
    const locationEvents = events.filter(
      (event) => event.eventType === "Location Updated"
    );
    const lastLocationEvent = locationEvents[locationEvents.length - 1];
    return lastLocationEvent
      ? (lastLocationEvent.metadata as { city: string; state: string })
      : null;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 justify-between flex">
        <h1 className="text-3xl font-bold mb-2">Line Haul Loads</h1>
        {(orgType === "Receiver" || orgType === "Shipper") && (
          <Button
            onClick={() => {
              setSelectedLoad(null);
              setIsAddLoadModalOpen(true);
            }}
            className="flex items-center gap-2"
          >
            <Truck className="h-4 w-4" />
            Create New Load
          </Button>
        )}
      </div>
      {/* Load Statistics */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card
          className={cn(
            "hover:cursor-pointer",
            filter === "All" && "bg-gray-200/30"
          )}
          onClick={() => setFilter("All")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-semibold">
                  Total Loads
                </p>
                <p className="text-2xl font-bold">{loads?.length ?? 0}</p>
              </div>
              <Truck className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn(
            "hover:cursor-pointer",
            filter === "Awaiting Pickup" && "bg-gray-200/30"
          )}
          onClick={() => setFilter("Awaiting Pickup")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-semibold">
                  Loads Awaiting Pickup
                </p>
                <p className="text-2xl font-bold ">
                  {loads?.filter(
                    (l) =>
                      l.status === "Not Assigned" ||
                      l.status === "Assigned" ||
                      l.status === "Being Picked Up"
                  ).length ?? 0}
                </p>
              </div>
              <Warehouse className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn(
            "hover:cursor-pointer",
            filter === "Out For Delivery" && "bg-gray-200/30"
          )}
          onClick={() => setFilter("Out For Delivery")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-semibold">
                  Loads Being Delivered
                </p>
                <p className="text-2xl font-bol">
                  {loads?.filter((l) => l.status === "In Transit").length ?? 0}
                </p>
              </div>
              <TruckIcon className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card
          className={cn(
            "hover:cursor-pointer",
            filter === "Completed" && "bg-gray-200/30"
          )}
          onClick={() => setFilter("Completed")}
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-semibold">
                  Completed Loads
                </p>
                <p className="text-2xl font-bold">
                  {loads?.filter((l) => l.status === "Delivered").length ?? 0}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Loads List */}
      <div className="grid grid-cols-2 gap-4">
        {loads &&
        loads.filter((l) => {
          if (filter === "All") return true;
          if (filter === "Awaiting Pickup")
            return (
              l.status === "Not Assigned" ||
              l.status === "Assigned" ||
              l.status === "Being Picked Up"
            );
          if (filter === "Out For Delivery") return l.status === "In Transit";
          if (filter === "Completed") return l.status === "Delivered";
          return false;
        }).length > 0 ? (
          loads
            .filter((l) => {
              if (filter === "All") return true;
              if (filter === "Awaiting Pickup")
                return (
                  l.status === "Not Assigned" ||
                  l.status === "Assigned" ||
                  l.status === "Being Picked Up"
                );
              if (filter === "Out For Delivery")
                return l.status === "In Transit";
              if (filter === "Completed") return l.status === "Delivered";
              return false;
            })
            .map((load) => {
              const assignedCarrier = load.carrierName;
              const badgeColor = getBadgeColor(load.status);

              return (
                <Card
                  key={load.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    setSelectedLoad(load);
                    setIsDetailViewOpen(true);
                  }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{load.name}</CardTitle>
                        <Badge variant={"warning"} className={badgeColor}>
                          {load.status}
                        </Badge>
                      </div>
                      {(orgType === "Broker" || orgType === "Receiver") && (
                        <Button
                          variant="outline"
                          className="ml-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLoad(load);
                            setIsAddClaimModalOpen(true);
                          }}
                        >
                          Create Claim
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 min-h-[245px]">
                    {/* Created on*/}
                    <div className="flex items-center gap-1 text-sm">
                      <CalendarDays className="h-4 w-4" />
                      <span>
                        Created: {load.createdAt.toLocaleDateString()}
                      </span>
                    </div>
                    {/* Route */}
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">
                        {load.sourceLocation} → {load.destinationLocation}{" "}
                        {lastLocation(load.events) != null ? (
                          <p>
                            Last updated: {lastLocation(load.events)?.city},{" "}
                            {lastLocation(load.events)?.state}
                          </p>
                        ) : (
                          <p>Last updated: No Updates</p>
                        )}
                      </span>
                    </div>
                    {/* Times */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">
                          Pickup Window:{" "}
                          {load.pickupStart && load.pickupEnd
                            ? formatDate(load.pickupStart) +
                              " - " +
                              formatTime(load.pickupEnd)
                            : "No pickup date set"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-6">
                      <span className="text-sm text-muted-foreground">
                        Drop-off Window:{" "}
                        {load.deliveryStart && load.deliveryEnd
                          ? formatDate(load.deliveryStart) +
                            " - " +
                            formatTime(load.deliveryEnd)
                          : "No delivery date set"}
                      </span>
                    </div>
                    {/* Load Details */}
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Weight className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">
                          {load.weight
                            ? formatWeight(load.weight)
                            : "No weight set."}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{load.pieces} pieces</span>
                      </div>
                    </div>
                    <Separator />
                    {/* Assignment Status */}
                    <div className="flex items-center gap-3">
                      {load.status !== "Not Assigned" && assignedCarrier ? (
                        <>
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={"/placeholder.svg"} />
                            <AvatarFallback>
                              {assignedCarrier
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-md font-medium">
                              {assignedCarrier}
                            </p>
                            <p className="text-xs">{load.driverName}</p>
                          </div>
                        </>
                      ) : (
                        <div className="flex">
                          <span className="text-sm text-muted-foreground">
                            No Carrier Assigned
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>

                  {load.status != "Delivered" &&
                    (orgType === "Shipper" || orgType === "Receiver") && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLoad(load);
                          setIsEditViewOpen(true);
                        }}
                      >
                        Edit Load
                      </Button>
                    )}
                </Card>
              );
            })
        ) : (
          <div className="col-span-1 lg:col-span-2">
            <Card className="p-6 text-center">
              <CardContent>
                <p className="text-lg text-muted-foreground">
                  No loads found for the selected filter.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      {/* All Modals */}
      {orgType === "Broker" && selectedLoad && isDetailViewOpen && (
        <AssignEditLoadModal
          load={selectedLoad}
          isOpen={isDetailViewOpen}
          setIsOpen={setIsDetailViewOpen}
          badgeColor={getBadgeColor(selectedLoad?.status || "")}
        />
      )}
      {(orgType === "Receiver" || orgType === "Vendor") &&
        selectedLoad &&
        isDetailViewOpen && (
          <ViewLoadModal
            load={selectedLoad}
            isOpen={isDetailViewOpen}
            setIsOpen={setIsDetailViewOpen}
            badgeColor={getBadgeColor(selectedLoad?.status || "")}
          />
        )}
      {selectedLoad && isEditViewOpen && (
        <UpdateLoadModal
          load={selectedLoad}
          isOpen={isEditViewOpen}
          setIsOpen={setIsEditViewOpen}
        />
      )}
      {isAddLoadModalOpen && (
        <AddLoadModal
          isOpen={isAddLoadModalOpen}
          setIsOpen={setIsAddLoadModalOpen}
        />
      )}
      {selectedLoad && isAddClaimModalOpen && (
        <AddClaimModal
          loadId={selectedLoad?.id}
          loadNameProp={selectedLoad?.name}
          isOpen={isAddClaimModalOpen}
          setIsOpen={setIsAddClaimModalOpen}
        />
      )}
    </div>
  );
}
